import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { Radio } from "@/components/Radio/Radio";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { PreviewRerunMode } from "../schema/listPreview";

interface RerunPreviewModalProps {
	isOpen: boolean;
	isLoading: boolean;
	onClose: () => void;
	onConfirm: (mode: PreviewRerunMode) => void | Promise<unknown>;
}

export const RerunPreviewModal: FunctionComponent<RerunPreviewModalProps> = ({
	isOpen,
	isLoading,
	onClose,
	onConfirm,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.preview.rerunModal.${key}`);
	const [mode, setMode] = useState<PreviewRerunMode>(PreviewRerunMode.KEEP_FOUND);
	// useSingleFlight sets its ref synchronously, so a second click 9 ms later is dropped before
	// `isLoading` re-renders; `isRunning` also shows the button busy in that window (PREC-5107 QA K).
	const { run, isRunning } = useSingleFlight();

	return (
		<Modal
			handleClose={onClose}
			handleSave={() => run(() => onConfirm(mode))}
			isLoading={isLoading || isRunning}
			isOpened={isOpen}
			submitButtonText={t("list.preview.rerunPreview")}
			title={ts("title")}
		>
			<div className="py-2">
				<p className="text-sm text-text-meta mb-4">{ts("description")}</p>

				<div className="flex flex-col gap-3">
					<div
						className={`rounded-lg border p-4 cursor-pointer ${
							mode === PreviewRerunMode.KEEP_FOUND
								? "border-blue-action bg-blue-action/5"
								: "border-border-light"
						}`}
						onClick={() => setMode(PreviewRerunMode.KEEP_FOUND)}
					>
						<div className="flex items-center gap-3">
							<Radio
								isChecked={mode === PreviewRerunMode.KEEP_FOUND}
								name="rerun-mode"
								onChange={() => setMode(PreviewRerunMode.KEEP_FOUND)}
							/>
							<div>
								<div className="text-sm font-medium text-text">{ts("keepFound")}</div>
								<div className="text-xs text-text-meta mt-0.5">{ts("keepFoundDescription")}</div>
							</div>
						</div>
					</div>

					<div
						className={`rounded-lg border p-4 cursor-pointer ${
							mode === PreviewRerunMode.DISCARD_ALL
								? "border-blue-action bg-blue-action/5"
								: "border-border-light"
						}`}
						onClick={() => setMode(PreviewRerunMode.DISCARD_ALL)}
					>
						<div className="flex items-center gap-3">
							<Radio
								isChecked={mode === PreviewRerunMode.DISCARD_ALL}
								name="rerun-mode"
								onChange={() => setMode(PreviewRerunMode.DISCARD_ALL)}
							/>
							<div>
								<div className="text-sm font-medium text-text">{ts("discardAll")}</div>
								<div className="text-xs text-text-meta mt-0.5">{ts("discardAllDescription")}</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</Modal>
	);
};
